import { useState, useEffect } from 'react';
import useUsers from '../hooks/useUsers';
import { useAuthContext } from '../context/AuthContext';

const roles = ['cms-admin', 'cms-editor', 'cms-viewer'];

export default function Users() {
  const { user } = useAuthContext();
  const usersApi = useUsers();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'cms-viewer' });
  const [localItems, setLocalItems] = useState([]);

  const isAdmin = user?.groups?.includes('cms-admin');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await usersApi.listUsers();
      const users = result.items || [];
      setItems(users);
      setLocalItems(users.map(item => ({ 
        ...item, 
        role: item.groups?.[0] || 'cms-viewer' 
      })));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const handleInvite = async () => {
    try {
      await usersApi.inviteUser({
        email: inviteForm.email,
        groups: [inviteForm.role],
      });
      setInviteForm({ email: '', role: 'cms-viewer' });
      await loadUsers();
    } catch (err) {
      setError(err);
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      await usersApi.updateUser(userId, { groups: [role] });
      await loadUsers();
    } catch (err) {
      setError(err);
    }
  };

  if (!isAdmin) {
    return <section className="card"><div className="alert warning">Only administrators can manage users.</div></section>;
  }

  return (
    <div className="stack gap-lg">
      <section className="card stack gap-md">
        <div>
          <h2>Invite user</h2>
          <p className="muted">Send a Cognito invitation email with a temporary password.</p>
        </div>
        {error ? <div className="alert error">{error.message}</div> : null}
        <div className="form-grid">
          <label>
            Email
            <input value={inviteForm.email} onChange={(event) => setInviteForm((current) => ({ ...current, email: event.target.value }))} />
          </label>
          <label>
            Role
            <select value={inviteForm.role} onChange={(event) => setInviteForm((current) => ({ ...current, role: event.target.value }))}>
              {roles.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
          </label>
        </div>
        <div>
          <button className="button primary" onClick={handleInvite}>
            Invite user
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Users</h2>
        {loading ? <p>Loading users…</p> : null}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(localItems.length > 0 ? localItems : items).map((item) => (
                <tr key={item.username || item.userId}>
                  <td>{item.email}</td>
                  <td>{item.status}</td>
                  <td>
                    <select
                      value={item.role || item.groups?.[0] || 'cms-viewer'}
                      onChange={(event) => {
                        const role = event.target.value;
                        setLocalItems((current) => current.map((entry) => 
                          (entry.username === item.username || entry.userId === item.userId) 
                            ? { ...entry, role } 
                            : entry
                        ));
                      }}
                    >
                      {roles.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </td>
                  <td>
                    <button 
                      className="button secondary small" 
                      onClick={() => handleUpdateRole(
                        item.userId || item.username, 
                        item.role || item.groups?.[0]
                      )}
                    >
                      Save role
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
